import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import MainLayout from '../../mainLayout';
import styled, { useTheme } from 'styled-components/native';
import { tr } from '../../../translations';
import ChatComponent from '../../../components/chat';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import ChatFooter from '../../../components/chat/chatFooter';
import {
  Dimensions,
  Platform,
  Keyboard,
  Animated,
  UIManager,
  KeyboardEvent,
} from 'react-native';
import { RootState, store } from '../../../redux/store';
import { connect, useDispatch } from 'react-redux';
import { ChatProps } from '../../../interfaces/screens/chat';
import { Theme } from '../../../theme/lightTheme';
import { useFocusEffect } from '@react-navigation/native';
import {
  onStreamingMessageEndAnimation,
  resetChat,
  sendMessage,
  getMessagesHistory,
  submitQNAStreaming,
  shareChat,
  toggleChatFavorite,
  getDevMessages,
} from './actions';
import {
  selectChat,
  setMessages,
  setLoadingMessages,
  setLoadingMessagesEnd,
  toggleChatFavoriteAction,
  removeChat,
  setTabId,
  setSelectedChatLayoutDirection,
  setSelectedChatFavorite,
} from './reducer';
import useChatVoiceRecognition from '../../../utils/hooks/useChatVoiceRecognition';
import { ChatScreenRef } from '../../../interfaces/components/chatComponent';
import { ChatItemType } from '../../../interfaces/reducers/chat';
import { ThreadItemType } from '../../../interfaces/api/qna';
import AndroidSoftInput from '../../../utils/androidSoftInput';
import { stopStreamingSSE } from '../../../api/content/qna';
import { sleep } from '../../../utils/helpers';
import { useCustomHeaderHeight } from '../../../utils/hooks/useHeaderHeight';
import { ChatType, ToastType } from '../../../utils/enums';
import { updateConfig } from '../../auth/login/actions';
import { ChatCategory } from '../../../interfaces/api/auth/login';
import { getCategoriesFromTabs } from '../../../utils/getCategoriesFromTabs';
import LinearGradient from 'react-native-linear-gradient';
import ChatHeader from '../../../components/chat/chatHeader';
import { toggleSettingsModal } from '../../../connected-components/settingsModal/actions';
import ChatHistory from '../../../components/chat/chatHistory';
import RecordView from '../../../components/chat/recordView';
import type { MessageItem } from '../../../interfaces/reducers/chat';
import { useTTSState } from '../../../utils/services/TTSService/useTTSState';
import removeMd from 'remove-markdown';
import { showGlobalToast } from '../../../connected-components/toast/actions';
import Icon from '../../../components/icon';
import DebugModal from '../../../components/debugModal';
import Config from 'react-native-config';
import { getCategoryKeyByIdFallback } from '../../../utils/categoryKeys';

// Function to clean markdown content for TTS reading
const cleanMarkdownForTTS = (markdownText: string): string => {
  if (!markdownText) return '';

  // First remove markdown formatting
  let cleaned = removeMd(markdownText);

  // Remove table separators (lines with only dashes, pipes, and spaces)
  cleaned = cleaned.replace(/^\s*\|[\s\-|]*\|\s*$/gm, '');

  // Remove empty table rows (lines with only pipes and spaces)
  cleaned = cleaned.replace(/^\s*\|[\s|]*\|\s*$/gm, '');

  // Remove multiple consecutive dashes and replace with spaces
  cleaned = cleaned.replace(/-+/g, ' ');

  // Remove multiple consecutive pipes and replace with spaces
  cleaned = cleaned.replace(/\|+/g, ' ');

  // Clean up multiple spaces
  cleaned = cleaned.replace(/\s+/g, ' ');

  // Remove leading/trailing whitespace from each line
  cleaned = cleaned.split('\n').map(line => line.trim()).join('\n');

  // Remove empty lines
  cleaned = cleaned.replace(/\n\s*\n/g, '\n');

  return cleaned.trim();
};

const Container = styled.View<{ heightInset: number; width: number }>`
  width: ${({ width }) => width}px;
  flex-grow: 1;

  ${({ heightInset }) =>
    Platform.OS === 'ios' &&
    `height: ${Dimensions.get('screen').height - (heightInset || 0)}px;`}
`;

// Add new styled components for web side menu
export const WebSideMenuContainer = styled.View<{
  isVisible: boolean;
  width: number;
  height: number;
}>`
  display: flex;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  background-color: ${({ theme }: { theme: any }) => theme.screenBackground};
  border-right: 1px solid ${({ theme }: { theme: any }) => theme.text.grey + '44'};
  overflow-y: auto;
  overflow-x: hidden;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible, width }) =>
    isVisible ? 'translateX(0)' : `translateX(-${width}px)`};
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
`;

export const WebChatContainer = styled.View<{
  sideMenuWidth: number;
  isSideMenuVisible: boolean;
}>`
  display: flex;
  flex: 1;
  height: 100%;
  margin-left: ${({ sideMenuWidth, isSideMenuVisible }) =>
    (isSideMenuVisible && sideMenuWidth ? sideMenuWidth : 0)}px;
  transition: margin-left 0.3s ease-in-out;
`;

// Icon container styled component (same as ChatHeader)
const IconContainer = styled.TouchableOpacity<{ size: number }>`
  width: ${({ size }) => size * 1.3}px;
  height: ${({ size }) => size * 1.3}px;
  background-color: ${({ theme }) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({ theme }) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
  margin-right: ${({ theme }) => theme.text.s12}px;
`;


// Enable LayoutAnimation for Android
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

/**
 * Chat screen component
 *
 * @component
 * @param {ChatProps} props - The screen properties, including navigation for handling routing.
 * @returns {React.ReactElement} The rendered chat screen component.
 */
const Chat: React.FC<ChatProps> = ({ chat, auth, route }) => {
  const { wdp, hdp, isLargeScreen } = useResponsiveDimensions();
  const dispatch = useDispatch();

  // Legacy React Native state (keeping for backward compatibility)
  const {
    isLoading,
    data,
    isError,
    presentUsers,
    isMessageError,
    isMessageLoading,
    streamMessageLoadingId,
    status,
    threadId,
  } = chat || {};

  // New React JS compatible state
  const {
    messages,
    chats,
    selectedChatId,
    isLoadingMessage,
    isErrorVoice,
    references,
    metadata,
    selectedChatInFavorite,
    selectedChatLayoutDirection,
    showDebug,
  } = chat || {};

  const { tabs_with_categories } = auth.config || {};
  const theme = useTheme() as Theme;

  // On web, merge URL query params with route.params so categoryId, categoryKey, tab_id from ?categoryId=4&... are used
  const routeParams = useMemo(() => {
    const fromRoute = route?.params || {};
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.location?.search) {
      return fromRoute;
    }
    const search = new URLSearchParams(window.location.search);
    const fromQuery: Record<string, unknown> = {};
    const categoryIdQuery = search.get('categoryId');
    if (categoryIdQuery != null) {
      const parsed = parseInt(categoryIdQuery, 10);
      fromQuery.categoryId = Number.isNaN(parsed) ? categoryIdQuery : parsed;
    }
    const categoryKeyQuery = search.get('categoryKey');
    if (categoryKeyQuery != null) fromQuery.categoryKey = categoryKeyQuery;
    const tabIdQuery = search.get('tab_id');
    if (tabIdQuery != null) fromQuery.tab_id = tabIdQuery;
    // Prefer route.params over URL so programmatic FAB navigation (categoryId, categoryKey) wins on first press
    return { ...fromQuery, ...fromRoute };
  }, [route?.params]);

  const {
    isQNA = true,
    drugQuestion,
    isDrugs,
    tab_id = '1',
    categoryId,
    categoryKey, // Extract categoryKey from navigation params
  } = routeParams;

  // Extract categories for the current tab using tabs_with_categories
  const chat_categories = getCategoriesFromTabs(tabs_with_categories, tab_id);

  // Custom header height.
  const headerHeight = useCustomHeaderHeight();
  const { id: userId, first_name } = auth?.currentUser || {};
  const [question, setQuestion] = useState<string>('');
  const [recommendationTab, setRecommendationTab] = useState<
    ChatCategory | undefined
  >(undefined);
  const [files, setFiles] = useState<any[] | undefined>();
  const [isNewThread, setIsNewThread] = useState<boolean>(false);
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState<any[]>([]);
  const [lastMessage, setLastMessage] = useState<any>({
    question: drugQuestion || '',
    file: null,
  });
  // Active category state management
  const [activeCategoryId, setActiveCategoryId] = useState<number | undefined>(
    categoryId,
  );
  // CRITICAL FIX: Prioritize deriving categoryKey from categoryId (e.g., 4 for treatment options) for API consistency
  // Fallback to categoryKey from route params if categoryId doesn't map to a known key
  const derivedCategoryKey = getCategoryKeyByIdFallback(activeCategoryId);
  const activeCategoryKey = derivedCategoryKey || categoryKey;

  // Sync activeCategoryId if route params change (e.g. navigation from another screen)
  useEffect(() => {
    setActiveCategoryId(categoryId);
  }, [categoryId]);
  const flatListRef = useRef<ChatScreenRef>(null);
  const { isRecording, startListening, stopListening } =
    useChatVoiceRecognition(setQuestion);
  const [isSideMenuVisible, setIsSideMenuVisible] = useState(
    Platform.OS === 'web' && isLargeScreen,
  );
  const [userManuallyOpenedMenu, setUserManuallyOpenedMenu] = useState(false);
  const sideMenuWidth =
    Platform.OS === 'web' && isLargeScreen ? Math.min(wdp(30), 350) : 0;
  const translateY = useRef(new Animated.Value(0)).current;
  const displayMessages = (messages && messages.length > 0 ? messages : data) || [];

  // Track streaming state to control side menu visibility
  const isStreaming = isMessageLoading || !!streamMessageLoadingId;

  // TTS service hook
  const { isReading, startReading, stopReading } = useTTSState();

  // Effect to hide side menu when streaming starts (only if user hasn't manually opened it)
  useEffect(() => {
    if (Platform.OS === 'web' && isLargeScreen && isStreaming && isSideMenuVisible && !userManuallyOpenedMenu) {
      setIsSideMenuVisible(false);
    }
  }, [isStreaming, isLargeScreen, isSideMenuVisible, userManuallyOpenedMenu]);

  // Reset user preference when streaming ends
  useEffect(() => {
    if (!isStreaming && userManuallyOpenedMenu) {
      setUserManuallyOpenedMenu(false);
    }
  }, [isStreaming, userManuallyOpenedMenu]);

  useEffect(() => {
    if (selectedChatId && chats?.length) {
      const selected = chats.find(item => +item.id === +selectedChatId);
      if (selected) {
        // Immediately update selectedChatInFavorite when chat is found
        dispatch(setSelectedChatFavorite(selected.is_favorite || false));
      }
    }
  }, [selectedChatId, chats, dispatch]);

  // Configure keyboard animations for Android only
  useEffect(() => {
    // Initialize new React JS features
    initializeNewFeatures();

    if (Platform.OS === 'android') {
      // Set initial state
      translateY.setValue(0);

      // Custom animation configuration to reduce flickering
      const springConfig = {
        tension: 40,
        friction: 100,
        useNativeDriver: true,
      };

      // Listen for keyboard events to animate only the footer
      const keyboardDidShowListener = Keyboard.addListener(
        'keyboardDidShow',
        (event: KeyboardEvent) => {
          // Use spring animation for smoother transition
          Animated.spring(translateY, {
            toValue: -event.endCoordinates.height,
            ...springConfig,
          }).start();
        },
      );

      const keyboardDidHideListener = Keyboard.addListener(
        'keyboardDidHide',
        () => {
          // Use spring animation for smoother transition
          Animated.spring(translateY, {
            toValue: 0,
            ...springConfig,
          }).start();
        },
      );

      return () => {
        keyboardDidShowListener.remove();
        keyboardDidHideListener.remove();
      };
    }
  }, []);

  // Initialize new React JS features
  const initializeNewFeatures = async () => {
    try {
      // Load chat categories
      // await getCategories(1);

      // Set initial tab ID if available
      if (tab_id) {
        dispatch(setTabId(tab_id.toString()));
      }

      // Set layout direction
      const layoutDirection = (
        route?.params && 'layout' in route.params ? route.params.layout : 'ltr'
      ) as 'ltr' | 'rtl';
      dispatch(setSelectedChatLayoutDirection(layoutDirection));
      } catch (error) {
        // Error initializing new features
      }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'android') {
        AndroidSoftInput.setSoftInputMode('stateVisible|adjustNothing');
      } else if (Platform.OS === 'ios') {
        // Original iOS behavior
      }

      return () => {
        if (Platform.OS === 'android') {
          AndroidSoftInput.setSoftInputMode('stateHidden|adjustPan');
        }
        resetThread();
        setQuestion('');
      };
    }, []),
  );

  const onHistoryItemPress = useCallback(
    async (item: ThreadItemType | ChatItemType) => {
      setRecommendationTab(undefined);
      setIsNewThread(true);

      if (!item?.id) return;
      try {
        dispatch(
          setMessages({
            messages: [],
            metadata: undefined,
          }),
        );
        const chatId = item.model_id || item.id;
        dispatch(setLoadingMessages());
        const messagesResult = await getMessagesHistory(
          Number(chatId),
          0,
          50,
          '',
        );
        dispatch(
          setMessages({
            messages: messagesResult?.messages || [],
            metadata: messagesResult?.metadata,
          }),
        );
        dispatch(selectChat(chatId.toString()));
        await sleep(100);
        flatListRef?.current?.scrollToEnd(false);
      } catch (error) {
        if (__DEV__) {
        }
        // Clear messages to avoid showing stale data
        dispatch(
          setMessages({
            messages: [],
            metadata: undefined,
          }),
        );
        dispatch(selectChat((item.model_id || item.id).toString()));
      } finally {
        dispatch(setLoadingMessagesEnd());
      }
    },
    [dispatch],
  );

  const resetThread = async () => {
    if (__DEV__) {
    }
    // stop both legacy SSE and new fetch streaming
    try {
      stopStreamingSSE();
    } catch (e) {
      if (__DEV__) {
      }
    }
    if (typeof window !== 'undefined' && window.streamController) {
      try {
        window.streamController.abort();
      } catch (e) {
        // ignore
      }
    }
    await sleep(100);
    resetChat();
    resetChat();
    setRecommendationTab(undefined);
    setActiveCategoryId(undefined);
    stopListening();
  };

  /**
   * Handles sending a message. Sets the last message, sends it, and resets the input fields.
   */
  const onSend = async () => {
    const tempQuestion = question;
    setQuestion('');
    setLastMessage({ question: tempQuestion, file: files });
    await stopListening();
    setFiles(undefined);

    // Use new React JS compatible function if available, fallback to legacy
    try {
      const body = {
        question: tempQuestion,
        specialty_id: auth?.currentUser?.specialty_id || -1,
        type: isDrugs ? ChatType.Drugs : ChatType.QNA,
        user_id: userId || -1,
        thread_id: (selectedChatId || threadId)?.toString(),
        question_id: categoryId || undefined,
        tab_id: tab_id || '1',
      };

      if (Platform.OS === 'web' && isQNA && !isDrugs) {
        await submitQNAStreaming(body);
      } else {
        await sendMessage(body);
      }
    } catch (error) { }

    if (isNewThread) {
      setIsNewThread(false);
    }
  };

  /**
   * Handles sending a message. Sets the last message, sends it, and resets the input fields.
   */
  const onQuestionPressedHandler = async (item: ChatCategory) => {
    setActiveCategoryId(item.id);
    stopListening();
    setLastMessage({ question, file: files });
    setQuestion('');
    setFiles(undefined);

    // Use new React JS compatible function if available, fallback to legacy
    try {
      const body = {
        question,
        specialty_id: auth?.currentUser?.specialty_id || -1,
        type: isDrugs ? ChatType.Drugs : ChatType.QNA,
        user_id: userId || -1,
        thread_id: (selectedChatId || threadId)?.toString(),
        question_id: item.id,
        tab_id: tab_id || '1',
      };

      if (Platform.OS === 'web' && isQNA && !isDrugs) {
        await submitQNAStreaming(body);
      } else {
        await sendMessage(body);
      }
    } catch (error) { }

    if (isNewThread) {
      setIsNewThread(false);
    }
  };

  /**
   * Start Record Recognition.
   */
  const onRecordPressHandler = () => {
    if (isRecording) {
      stopListening();
    } else {
      startListening();
    }
  };

  /**
   * Tries to resend the last message in case of failure.
   */
  const onMessageTryAgainHandler = async () => {
    stopListening();
    setLastMessage({ question, file: files });
    setQuestion('');
    setFiles(undefined);

    // Use new React JS compatible function if available, fallback to legacy
    try {
      const body = {
        question: lastMessage?.question || '',
        specialty_id: auth?.currentUser?.specialty_id || -1,
        type: isDrugs ? ChatType.Drugs : ChatType.QNA,
        user_id: userId || -1,
        thread_id: (selectedChatId || threadId)?.toString(),
        question_id: categoryId || undefined,
        tab_id: tab_id || '1',
      };

      if (Platform.OS === 'web' && isQNA && !isDrugs) {
        await submitQNAStreaming(body);
      } else {
        await sendMessage(body);
      }
    } catch (error) { }

    if (isNewThread) {
      setIsNewThread(false);
    }
  };

  const onChatRefresh = useCallback(async () => {
    if (selectedChatId || threadId) {
      try {
        const chatId = selectedChatId || threadId;
        if (chatId) {
          const messagesResult = await getMessagesHistory(
            Number(chatId),
            0,
            50,
            '',
          );
          if (messagesResult.messages.length > 0) {
            dispatch(
              setMessages({
                messages: messagesResult.messages,
                metadata: messagesResult.metadata,
              }),
            );
            flatListRef?.current?.scrollToEnd(false);
            return;
          }
        }
      } catch (error) {
        if (__DEV__) {
        }
      }
    } else {
      resetThread();
    }
  }, [selectedChatId, threadId, dispatch]);

  // New React JS compatible functions
  const onShareChat = async () => {
    const chatId = selectedChatId || threadId;
    if (chatId) {
      await shareChat(chatId.toString());
    }
  };

  const onToggleFavorite = async () => {
    const newFavoriteValue = selectedChatInFavorite ? false : true;
    const chatId = selectedChatId || threadId;
    if (chatId) {
      // Find the selected chat to get favorite_id
      const selectedChat = chats.find(chat => +chat.id === +chatId) as ChatItemType;

      // For adding to favorites: use chatId (model_id)
      // For removing from favorites: use favorite_id
      let idToUse: number;
      if (newFavoriteValue) {
        // Adding to favorites - use chatId (model_id)
        idToUse = Number(chatId);
      } else {
        // Removing from favorites - need favorite_id
        // If selectedChat exists and has favorite_id, use it; otherwise use chatId
        idToUse = selectedChat?.favorite_id || Number(chatId);
      }

      const res = await toggleChatFavorite(
        idToUse,
        newFavoriteValue,
        tab_id,
      );
      if (res) {
        dispatch(
          toggleChatFavoriteAction({
            id: chatId,
            isFavorite: newFavoriteValue,
            favorite_id: res.content?.model?.favorite_id || (newFavoriteValue ? res.content?.model?.favorite_id : null),
            chatId: chatId,
          }),
        );
        // Immediately update selectedChatInFavorite state
        dispatch(setSelectedChatFavorite(newFavoriteValue));
      }
    }
  };

  // Message options callback functions
  const onReadTextPress = useCallback(
    async (message: MessageItem) => {
      if (isReading) {
        await stopReading();
      } else {
        const textContent = cleanMarkdownForTTS(message?.message || '');
        await startReading(textContent);
      }
    },
    [isReading, startReading, stopReading],
  );

  // Cleanup TTS when component unmounts or user leaves screen
  useEffect(() => {
    return () => {
      stopReading();
    };
  }, []);

  // Use focus effect to stop TTS when user leaves the screen
  useFocusEffect(
    useCallback(() => {
      return () => {
        stopReading();
      };
    }, []),
  );

  const onSharePress = useCallback(async () => {
    try {
      // Always share the entire chat
      await onShareChat();
    } catch (error) {
      if (__DEV__) {
      }
    }
  }, [onShareChat]);
  // Use new messages array if available, fallback to legacy data
  const displayIsLoading = isLoadingMessage || isLoading;
  const displayIsError = isErrorVoice || isError;

  // Check environment variable to control debug button visibility
  // Priority: ENV variable > API response (Redux state)
  // showDebug comes from API response (res?.content?.show_debug) or when dev_message_id is present
  // If SHOW_DEBUG_BUTTON env var is set, it overrides the API response
  const shouldShowDebug = Config.SHOW_DEBUG_BUTTON === 'true'
    ? true
    : Config.SHOW_DEBUG_BUTTON === 'false'
      ? false
      : showDebug; // Fallback to Redux state (from API response) if env var is not set

  const onDebugButtonPress = useCallback(
    async (message: MessageItem) => {
      if (!message?.dev_message_id) {
        showGlobalToast({
          message: tr('app.error'),
          type: ToastType.ERROR,
        });
        return;
      }
      try {
        const data = await getDevMessages(message.dev_message_id);
        setDebugData(data || []);
        setIsDebugModalOpen(true);
      } catch (error) {
        if (__DEV__) {
        }
        showGlobalToast({
          message: tr('app.error'),
          type: ToastType.ERROR,
        });
      }
    },
    [],
  );

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      enableScroll={false}
      noPadding
      keyboardAvoidScrollView={Platform.OS === 'ios'}
      extraScrollHeight={-hdp(1)}
      noPaddingHeader>
      <RecordView
        isRecording={isRecording}
        question={question}
        onClose={stopListening}
        onSend={onSend}
      />
      <ChatHeader
        customLeftIcon={() =>
          Platform.OS === 'web' && isLargeScreen ? (
            <IconContainer
              activeOpacity={0.8}
              onPress={() => {
                setIsSideMenuVisible(!isSideMenuVisible);
                setUserManuallyOpenedMenu(!isSideMenuVisible);
              }}
              size={theme.text.s1}>
              <Icon
                name={isSideMenuVisible ? "collapse" : "expand"}
                color={theme.secondary}
                size={theme.text.s5}
              />
            </IconContainer>
          ) : (
            <></>
          )
        }
        onHistoryPress={() =>
          Platform.OS === 'web' && isLargeScreen
            ? (() => {
              setIsSideMenuVisible(prev => !prev);
              setUserManuallyOpenedMenu(true);
            })()
            : toggleSettingsModal({
              title: tr(`chat.chatHistory`),
              component: () => (
                <ChatHistory
                  isQNA={isQNA}
                  onChatItemPressed={onHistoryItemPress}
                  selectedChatId={(selectedChatId || threadId)?.toString()}
                  onCreateNewChatPressed={resetThread}
                  tab_id={tab_id}
                />
              ),
            })
        }
      />
      {Platform.OS === 'web' && isLargeScreen ? (
        <div style={{ display: 'flex', width: '100%', height: '100%', position: 'relative' }}>
          <WebSideMenuContainer
            isVisible={isSideMenuVisible}
            width={sideMenuWidth}
            height={hdp(100)}>
            <ChatHistory
              isQNA={isQNA}
              onChatItemPressed={onHistoryItemPress}
              selectedChatId={(selectedChatId || threadId)?.toString()}
              onCreateNewChatPressed={resetThread}
              tab_id={tab_id}
            />
          </WebSideMenuContainer>
          <WebChatContainer sideMenuWidth={sideMenuWidth} isSideMenuVisible={isSideMenuVisible}>
            {Platform.OS === 'web' ? (
              <LinearGradient
                colors={[
                  theme.chat.startLinearGradient +
                  (displayMessages.length > 0 ? '55' : ''),
                  theme.chat.endLinearGradient + (displayMessages.length > 0 ? '55' : ''),
                ]}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <ChatComponent
                  //@ts-ignore
                  ref={flatListRef}
                  userId={userId + ''}
                  userName={first_name || ''}
                  data={displayMessages}
                  isWriting={status?.isTyping && userId !== status?.userId}
                  isLoading={displayIsLoading && displayMessages.length === 0}
                  isLoadingMore={displayIsLoading && displayMessages.length > 20}
                  isError={displayIsError}
                  isMessageSendingError={isMessageError}
                  isMessageSendingLoading={isMessageLoading}
                  streamMessageLoadingId={streamMessageLoadingId}
                  onMessageTryAgain={onMessageTryAgainHandler}
                  onRefresh={onChatRefresh}
                  onStreamingMessageEndAnimation={
                    onStreamingMessageEndAnimation
                  }
                  onlineUsers={presentUsers}
                  onCheckAnswerPressed={onQuestionPressedHandler}
                  question={question}
                  setQuestion={setQuestion}
                  onRecordHandler={onRecordPressHandler}
                  isRecording={isRecording}
                  isErrorAnswer={displayIsError}
                  introQuestions={chat_categories || []}
                  onIntroQuestionsRefreshPress={updateConfig}
                  selectedRecommendationTab={recommendationTab}
                  onRecommendationClosePress={() => {
                    setRecommendationTab(undefined);
                  }}
                  isQNA={isQNA}
                  onQuestionPress={item => setRecommendationTab(item)}
                  // Add new React JS compatible props if ChatComponent supports them
                  references={references}
                  metadata={metadata}
                  layoutDirection={selectedChatLayoutDirection}
                  onDebugButtonPress={onDebugButtonPress}
                  showDebug={shouldShowDebug}
                  // Message options props
                  onSharePress={onSharePress}
                  onAddToFavorite={onToggleFavorite}
                  isFavorite={selectedChatInFavorite}
                  shared={false}
                  onReadTextPress={onReadTextPress}
                  isSpeaking={isReading}
                  categoryId={activeCategoryId}
                  categoryKey={activeCategoryKey || undefined}
                  tab_id={tab_id}
                />
                {(displayMessages.length > 0 || isQNA || !!recommendationTab) &&
                  !(categoryId !== undefined && displayMessages.length > 0) && (
                    <ChatFooter
                      value={question}
                      onChangeText={setQuestion}
                      onAttachPress={onRecordPressHandler}
                      isAttachBadge={isRecording}
                      isLoading={displayIsLoading || isMessageLoading}
                      placeholder={
                        selectedChatId || threadId
                          ? tr('chat.sendFollowPlaceholder')
                          : tr('chat.sendPlaceholder')
                      }
                      onSubmit={onSend}
                      isRecording={isRecording}
                    />
                  )}
              </LinearGradient>
            ) : (
              <LinearGradient
                style={{ flex: 1 }}
                colors={
                  displayMessages.length > 0
                    ? [
                      theme.chat.startLinearGradient + '55',
                      theme.chat.endLinearGradient + '55',
                    ]
                    : [
                      theme.chat.startLinearGradient,
                      theme.chat.endLinearGradient,
                    ]
                }>
                <ChatComponent
                  //@ts-ignore
                  ref={flatListRef}
                  userId={userId + ''}
                  userName={first_name || ''}
                  data={displayMessages}
                  isWriting={status?.isTyping && userId !== status?.userId}
                  isLoading={displayIsLoading && displayMessages.length === 0}
                  isLoadingMore={displayIsLoading && displayMessages.length > 20}
                  isError={displayIsError}
                  isMessageSendingError={isMessageError}
                  isMessageSendingLoading={isMessageLoading}
                  streamMessageLoadingId={streamMessageLoadingId}
                  onMessageTryAgain={onMessageTryAgainHandler}
                  onRefresh={onChatRefresh}
                  onStreamingMessageEndAnimation={
                    onStreamingMessageEndAnimation
                  }
                  onlineUsers={presentUsers}
                  onCheckAnswerPressed={onQuestionPressedHandler}
                  question={question}
                  setQuestion={setQuestion}
                  onRecordHandler={onRecordPressHandler}
                  isRecording={isRecording}
                  isErrorAnswer={displayIsError}
                  introQuestions={chat_categories || []}
                  onIntroQuestionsRefreshPress={updateConfig}
                  selectedRecommendationTab={recommendationTab}
                  onRecommendationClosePress={() => {
                    setRecommendationTab(undefined);
                  }}
                  isQNA={isQNA}
                  onQuestionPress={item => setRecommendationTab(item)}
                  // Add new React JS compatible props if ChatComponent supports them
                  references={references}
                  metadata={metadata}
                  layoutDirection={selectedChatLayoutDirection}
                  onDebugButtonPress={onDebugButtonPress}
                  showDebug={shouldShowDebug}
                  // Message options props
                  onSharePress={onSharePress}
                  onAddToFavorite={onToggleFavorite}
                  isFavorite={selectedChatInFavorite}
                  shared={false}
                  onReadTextPress={onReadTextPress}
                  isSpeaking={isReading}
                  categoryId={categoryId}
                  categoryKey={categoryKey}
                  tab_id={tab_id}
                />
                {(displayMessages.length > 0 || isQNA || !!recommendationTab) &&
                  !(categoryId !== undefined && displayMessages.length > 0) && (
                    <ChatFooter
                      value={question}
                      onChangeText={setQuestion}
                      onAttachPress={onRecordPressHandler}
                      isAttachBadge={isRecording}
                      isLoading={displayIsLoading || isMessageLoading}
                      placeholder={
                        selectedChatId || threadId
                          ? tr('chat.sendFollowPlaceholder')
                          : tr('chat.sendPlaceholder')
                      }
                      onSubmit={onSend}
                      isRecording={isRecording}
                    />
                  )}
              </LinearGradient>
            )}
          </WebChatContainer>
        </div>
      ) : (
        <Container heightInset={headerHeight} width={wdp(100)}>
          <LinearGradient
            style={{ flex: 1 }}
            colors={
              displayMessages.length > 0
                ? [
                  theme.chat.startLinearGradient + '55',
                  theme.chat.endLinearGradient + '55',
                ]
                : [theme.chat.startLinearGradient, theme.chat.endLinearGradient]
            }>
            <ChatComponent
              //@ts-ignore
              ref={flatListRef}
              userId={userId + ''}
              userName={first_name || ''}
              data={displayMessages}
              isWriting={status?.isTyping && userId !== status?.userId}
              isLoading={displayIsLoading && displayMessages.length === 0}
              isLoadingMore={displayIsLoading && displayMessages.length > 20}
              isError={displayIsError}
              isMessageSendingError={isMessageError}
              isMessageSendingLoading={isMessageLoading}
              streamMessageLoadingId={streamMessageLoadingId}
              onMessageTryAgain={onMessageTryAgainHandler}
              onRefresh={onChatRefresh}
              onStreamingMessageEndAnimation={onStreamingMessageEndAnimation}
              onlineUsers={presentUsers}
              onCheckAnswerPressed={onQuestionPressedHandler}
              question={question}
              setQuestion={setQuestion}
              onRecordHandler={onRecordPressHandler}
              isRecording={isRecording}
              isErrorAnswer={displayIsError}
              introQuestions={chat_categories || []}
              onIntroQuestionsRefreshPress={updateConfig}
              selectedRecommendationTab={recommendationTab}
              onRecommendationClosePress={() => {
                setRecommendationTab(undefined);
              }}
              isQNA={isQNA}
              onQuestionPress={item => setRecommendationTab(item)}
              // Add new React JS compatible props if ChatComponent supports them
              references={references}
              metadata={metadata}
              layoutDirection={selectedChatLayoutDirection}
              onDebugButtonPress={onDebugButtonPress}
              showDebug={shouldShowDebug}
              // Message options props
              onSharePress={onSharePress}
              onAddToFavorite={onToggleFavorite}
              isFavorite={selectedChatInFavorite}
              shared={false}
              onReadTextPress={onReadTextPress}
              isSpeaking={isReading}
            />
            {(displayMessages.length > 0 || isQNA || !!recommendationTab) &&
              !(categoryId !== undefined && displayMessages.length > 0) && (
                <ChatFooter
                  style={{
                    opacity: Platform.OS === 'android' ? 0 : 1,
                    pointerEvents: Platform.OS === 'android' ? 'none' : 'auto',
                  }}
                  value={question}
                  onChangeText={setQuestion}
                  onAttachPress={onRecordPressHandler}
                  isAttachBadge={isRecording}
                  isLoading={displayIsLoading || isMessageLoading}
                  placeholder={
                    selectedChatId || threadId
                      ? tr('chat.sendFollowPlaceholder')
                      : tr('chat.sendPlaceholder')
                  }
                  onSubmit={onSend}
                  isRecording={isRecording}
                />
              )}
          </LinearGradient>
        </Container>
      )}
      {Platform.OS === 'android' &&
        (displayMessages.length > 0 || isQNA || !!recommendationTab) &&
        !(categoryId !== undefined && displayMessages.length > 0) && (
          <Animated.View
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              transform: [
                {
                  translateY: translateY,
                },
              ],
              zIndex: 10,
            }}>
            <ChatFooter
              style={{
                width: '100%',
              }}
              value={question}
              onChangeText={setQuestion}
              onAttachPress={onRecordPressHandler}
              isAttachBadge={isRecording}
              isLoading={displayIsLoading || isMessageLoading}
              placeholder={
                selectedChatId || threadId
                  ? tr('chat.sendFollowPlaceholder')
                  : tr('chat.sendPlaceholder')
              }
              onSubmit={onSend}
              isRecording={isRecording}
            />
          </Animated.View>
        )}
      <DebugModal
        isOpen={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        data={debugData}
      />
    </MainLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  chat: state.chat,
  auth: state.auth,
});
export default connect(mapStateToProps, null)(Chat);
